import {readInlineData, useLazyLoadQuery, useRelayEnvironment} from 'react-relay'
import type {DevelopmentSectionReposQuery} from './__generated__/DevelopmentSectionReposQuery.graphql'
import {
  RepositoryFragment,
  RepositoryPickerInternal,
  type RepositoryPickerRepository$data,
  type RepositoryPickerRepository$key,
  type RepositoryPickerTopRepositories$key,
} from '@github-ui/item-picker/repository'
import {DevelopmentSectionReposGraphqlQuery, useBranchesQueryRef, usePullRequestsQueryRef} from './DevelopmentSection'
import {useCallback, useMemo, useState} from 'react'
import {Button, Truncate} from '@primer/react'
import {PullRequestAndBranchPicker, type PullRequestPickerPullRequest$data} from '@github-ui/item-picker/pr'
import {ArrowLeftIcon} from '@primer/octicons-react'
import type {BranchPickerRef$data} from '@github-ui/item-picker/branch'
import {linkPullRequestsMutation} from '../../../mutations'
import {useToastContext} from '@github-ui/toast'
import {ERRORS, HOTKEYS} from '../../../constants'

import {useKeyPress} from '@github-ui/use-key-press'
import {useItemPickersContext} from '@github-ui/item-picker'
import {linkBranchesMutation} from '../../../mutations/link-branches'

export type DevelopmentSectionEditorNewBaseProp = {
  issueId: string
  linkedBranches: BranchPickerRef$data[]
  linkedPullRequests: PullRequestPickerPullRequest$data[]
  anchorElement: (props: React.HTMLAttributes<HTMLElement>) => JSX.Element
  isCreateBranchDialogOpen: boolean
  repoPickerSubtitle: React.ReactElement
  prAndBranchPickerSubtitle: React.ReactElement
  triggerOpen?: boolean
}

export type DevelopmentSectionEditorNewProps = DevelopmentSectionEditorNewBaseProp & {
  owner: string
  repo: string
  setCurrentRepo: (repo: {owner: string; name: string}) => void
}

export type DevelopmentSectionEditorNewInternalProps = DevelopmentSectionEditorNewBaseProp & {
  selectedRepository: RepositoryPickerRepository$data | undefined
  onSelectedRepository: (repo?: RepositoryPickerRepository$data) => void
  topRepositoriesData: RepositoryPickerTopRepositories$key
}

export const DevelopmentSectionEditorNew = ({
  owner,
  repo,
  setCurrentRepo,
  ...rest
}: DevelopmentSectionEditorNewProps) => {
  const repos = useLazyLoadQuery<DevelopmentSectionReposQuery>(DevelopmentSectionReposGraphqlQuery, {
    repo,
    owner,
  })

  // eslint-disable-next-line no-restricted-syntax
  const initialRepository = readInlineData<RepositoryPickerRepository$key>(RepositoryFragment, repos.repository)
  const [selectedRepository, setSelectedRepository] = useState<RepositoryPickerRepository$data | null | undefined>(
    initialRepository,
  )

  const onSelectedRepository = useCallback(
    (repository?: RepositoryPickerRepository$data) => {
      setSelectedRepository(repository)
      if (repository) {
        setCurrentRepo({owner: repository.owner.login, name: repository.name})
      }
    },
    [setCurrentRepo],
  )

  return (
    <DevelopmentSectionEditorNewInternal
      selectedRepository={selectedRepository ?? undefined}
      onSelectedRepository={onSelectedRepository}
      topRepositoriesData={repos.viewer}
      {...rest}
    />
  )
}

export const DevelopmentSectionEditorNewInternal = ({
  issueId,
  selectedRepository,
  onSelectedRepository,
  topRepositoriesData,
  anchorElement,
  repoPickerSubtitle,
  prAndBranchPickerSubtitle,
  isCreateBranchDialogOpen,
  linkedBranches,
  linkedPullRequests,
  triggerOpen = false,
}: DevelopmentSectionEditorNewInternalProps) => {
  const environment = useRelayEnvironment()
  const {addToast} = useToastContext()

  const onSelectRepository = useCallback(
    (repo?: RepositoryPickerRepository$data) => {
      if (repo !== selectedRepository) {
        onSelectedRepository(repo)
      }
      setShouldOpenPullRequestsAndBranchesPicker(true)
    },
    [onSelectedRepository, selectedRepository],
  )

  const repoNameWithOwner = useMemo(() => {
    if (selectedRepository) {
      return `${selectedRepository.owner.login}/${selectedRepository.name}`
    }
    return ''
  }, [selectedRepository])

  const pullRequestQueryRef = usePullRequestsQueryRef(selectedRepository)
  const branchesRequestQueryRef = useBranchesQueryRef(selectedRepository)
  const [shouldOpenPullRequestsAndBranchesPicker, setShouldOpenPullRequestsAndBranchesPicker] =
    useState<boolean>(triggerOpen)
  const [shouldOpenRepoPicker, setShouldOpenRepoPicker] = useState<boolean>(triggerOpen)

  const linkedPullRequestsForRepo = useMemo(() => {
    return linkedPullRequests.filter(pr => pr.repository.id === selectedRepository?.id)
  }, [linkedPullRequests, selectedRepository?.id])

  const linkedBranchesForRepo = useMemo(() => {
    return linkedBranches.filter(branch => branch.repository.id === selectedRepository?.id)
  }, [linkedBranches, selectedRepository?.id])

  const handleOnClick = useCallback(() => {
    onSelectRepository(undefined)
    setShouldOpenRepoPicker(true)
  }, [onSelectRepository])

  const {anyItemPickerOpen} = useItemPickersContext()
  const onHotKeyPress = useCallback(
    (e: KeyboardEvent) => {
      if (anyItemPickerOpen()) {
        return
      }
      // We don't want the filter to show the hotkey
      e.preventDefault()
      e.stopPropagation()
      handleOnClick()
    },
    [anyItemPickerOpen, handleOnClick],
  )

  useKeyPress([HOTKEYS.developmentSection], onHotKeyPress, {
    triggerWhenInputElementHasFocus: false,
    triggerWhenPortalIsActive: false,
  })

  //  listen to shortcuts here dooh
  const pullRequestAndBranchPickerTitle = useMemo(() => {
    return (
      <>
        <Button
          variant="invisible"
          leadingVisual={ArrowLeftIcon}
          size="small"
          title={'Back to repositories'}
          onClick={handleOnClick}
          sx={{
            color: 'fg.default',
            px: 2,
            '> [data-component="text"]': {
              overflow: 'hidden',
            }, // needed to truncate button text
          }}
        >
          <Truncate id="repo-name" title={repoNameWithOwner} sx={{fontSize: 2, maxWidth: 250}}>
            {repoNameWithOwner}
          </Truncate>
        </Button>
      </>
    )
  }, [handleOnClick, repoNameWithOwner])

  const onSelectionChange = useCallback(
    (selected: Array<PullRequestPickerPullRequest$data | BranchPickerRef$data>) => {
      const newItems = selected.filter(
        item =>
          [...linkedPullRequestsForRepo, ...linkedBranchesForRepo].find(prOrBranch => prOrBranch.id === item.id) ===
          undefined,
      )
      const existingItems = [...linkedPullRequests, ...linkedBranches].filter(
        item =>
          item.repository.id !== selectedRepository?.id ||
          selected.find(prOrBranch => prOrBranch.id === item.id) !== undefined,
      )

      const sortedItems = [...existingItems, ...newItems]
      const sortedPrs = sortedItems.filter(item => item.id.startsWith('PR_')) as PullRequestPickerPullRequest$data[]
      const sortedBranches = sortedItems.filter(item => item.id.startsWith('REF_')) as BranchPickerRef$data[]

      linkPullRequestsMutation({
        environment,
        input: {
          baseIssueOrPullRequestId: `${issueId}`,
          linkingIds: sortedPrs.map(pr => pr.id),
        },
        onError: () => {
          addToast({
            type: 'error',
            message: ERRORS.couldNotUpdateLinkedPullRequests,
          })
        },
        linkingPrs: sortedPrs,
      })

      linkBranchesMutation({
        environment,
        input: {
          baseIssueId: `${issueId}`,
          linkingIds: sortedBranches.map(branch => branch.id),
        },
        onError: () => {
          addToast({
            type: 'error',
            message: ERRORS.couldNotUpdateLinkedBranches,
          })
        },
      })
    },
    [
      addToast,
      environment,
      issueId,
      linkedBranches,
      linkedBranchesForRepo,
      linkedPullRequests,
      linkedPullRequestsForRepo,
      selectedRepository?.id,
    ],
  )

  return (
    <>
      {!selectedRepository && (
        <RepositoryPickerInternal
          initialRepository={selectedRepository}
          topRepositoriesData={topRepositoriesData}
          onSelect={onSelectRepository}
          anchorElement={anchorElement}
          title={'Link a branch or pull request'}
          subtitle={repoPickerSubtitle}
          preventClose={isCreateBranchDialogOpen}
          preventDefault={true}
          triggerOpen={shouldOpenRepoPicker}
        />
      )}
      {selectedRepository && pullRequestQueryRef && branchesRequestQueryRef && (
        <PullRequestAndBranchPicker
          repoNameWithOwner={repoNameWithOwner}
          pullRequestQueryRef={pullRequestQueryRef}
          initialSelectedPrs={linkedPullRequestsForRepo}
          initialSelectedBranches={linkedBranchesForRepo}
          branchesQueryRef={branchesRequestQueryRef}
          onSelectionChange={onSelectionChange}
          anchorElement={anchorElement}
          title={pullRequestAndBranchPickerTitle}
          subtitle={prAndBranchPickerSubtitle}
          triggerOpen={shouldOpenPullRequestsAndBranchesPicker}
          preventClose={isCreateBranchDialogOpen}
        />
      )}
    </>
  )
}

try{ DevelopmentSectionEditorNew.displayName ||= 'DevelopmentSectionEditorNew' } catch {}
try{ DevelopmentSectionEditorNewInternal.displayName ||= 'DevelopmentSectionEditorNewInternal' } catch {}